import React, { useRef, useState } from 'react';
import { gql } from '@apollo/client';
import DetailSection from '../DetailSection';
import sortByEndDate from '../utils/sortByEndDate';
import styled from 'styled-components';
import { buttonMixin, sectionMixin } from '../utils/viewMoreMixins';
import { Alert, CardButton, Grid } from '@zillow/constellation';
import ResidenceSection from './ResidenceSection';
import TagType from '../../../../../../constants/tagType';
import { useZgGraphqlQuery } from '../../../../../../hooks/useGraphqlQuery';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { onQueryError } from '../utils/onQueryError';
import { CardSpinner } from '../../../../../../components/CardSpinner';

export const QUERY = gql`
  query ResidenceHistory($propertyId: ID!, $applicationId: ID!, $applicantId: ID!, $zgAuthZone: ZgAuthZone!) {
    rentalListing(id: $propertyId) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          userId
          zgAuthZone
          answers {
            id
            currentResidence {
              id
              startDate
              type
              monthlyPayment
              address {
                street
                city
                state
              }
              ... on OtherResidence {
                description
              }
              ... on RentalResidence {
                manager {
                  name
                  email
                  phone
                }
              }
            }
            pastResidences {
              id
              startDate
              endDate
              type
              address {
                street
                city
                state
              }
              ... on OtherResidence {
                description
              }
              ... on RentalResidence {
                manager {
                  name
                  email
                  phone
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const ERROR_MSG = "Sorry, there was an error loading this applicant's residential history.";

const StyledResidenceSection = styled(ResidenceSection)`
  ${sectionMixin}
`;

const ViewMoreButton = styled(CardButton)`
  ${buttonMixin}
`;
export default function ResidenceHistory() {
  const [isExpanded, setIsExpanded] = useState(false);
  const queryVariables = useQueryVariables();
  const heading = 'Residence history';

  const residenceRef = useRef();
  const { data, isLoading, isError, error } = useZgGraphqlQuery(QUERY, queryVariables);

  if (isError) {
    onQueryError(error);
  }

  if (isLoading)
    return (
      <DetailSection heading={heading}>
        <CardSpinner />
      </DetailSection>
    );
  if (isError) return <Alert>{ERROR_MSG}</Alert>;

  const viewMoreOrLess = () => {
    setIsExpanded(!isExpanded);

    if (isExpanded) {
      residenceRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const residences = [
    data.rentalListing.landlordRentalApplication.applicant.answers.currentResidence,
    ...data.rentalListing.landlordRentalApplication.applicant.answers.pastResidences,
  ].sort(sortByEndDate);

  return (
    <DetailSection id="residence-history" heading="Residence history" ref={residenceRef} tagType={TagType.selfReported}>
      <>
        <Grid
          display="grid"
          rowGap="md"
          columnGap="lg"
          gridTemplateColumns={{
            default: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          }}
        >
          {residences.map((residence, index) => (
            <StyledResidenceSection key={index} residence={residence} isExpanded={isExpanded} index={index} />
          ))}
        </Grid>
        <ViewMoreButton
          numItems={residences.length}
          isExpanded={isExpanded}
          onClick={viewMoreOrLess}
          id="residence-history-view-more-button"
        >
          View {isExpanded ? 'less' : 'more'}
        </ViewMoreButton>
      </>
    </DetailSection>
  );
}
